import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { theme, useNavbarContainerHeight, useResponsive, VStack, webOnlyStyles } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../context/AuthMachine';
import { useCustomer } from '../../../context/Customer';
import { useResetStacksPostLogout } from '../../../routing/hooks/useResetStacksPostLogout';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import { AccountCustomerName } from './components/AccountCustomerName';
import { AccountMenuFooter } from './components/AccountMenuFooter';
import { AccountMenuGiftCardHeader } from './components/AccountMenuGiftCardHeader';
import { AccountMenuSignOutItem } from './components/AccountMenuSignOutItem';
import { AccountPrimaryMenu } from './components/AccountPrimaryMenu';
import { AccountSecondaryMenu } from './components/AccountSecondaryMenu';

import type { AccountTabScreenId } from '../Account.types';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenu = (props: Props) => {
  const { screen: screenId } = props;

  const { currentBreakpoint, match, minWidth } = useResponsive();
  const { height: navbarHeight } = useNavbarContainerHeight();

  const a11ySelector = useA11ySelector('account.links');
  const resetStacksPostLogout = useResetStacksPostLogout();

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const { isSigningOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();

  const isAccountUpdatesEnabled = useFeatureFlag('CELS-5485-order-account-updates-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldRenderGiftCardHeader = match([isAccountUpdatesEnabled, false]);
  const shouldRenderCustomerName = match([!isAccountUpdatesEnabled, true]);
  const dividerColor = match([theme.colors.DARK_KALE, theme.colors.LIGHT]);

  const menuContainerGap = match([theme.spacing['6'], theme.spacing['10']]);
  const menuContainerWebStyle = webOnlyStyles({
    position: 'sticky',
    top: isNavbarV2Enabled ? navbarHeight : 0,
  } as unknown as Parameters<typeof webOnlyStyles>[0]);
  const menuContainerResponsiveStyle = match([
    styles.menuContainerXS,
    [styles.menuContainerSM, menuContainerWebStyle],
  ]);

  const primaryMenuGap = match([isAccountUpdatesEnabled ? 0 : theme.spacing['4'], 0]);
  const nameContainerStyle = match([styles.nameContainerXS, styles.nameContainerSM]);
  const signOutItemContainerStyles =
    isAccountUpdatesEnabled && currentBreakpoint.isXS ? styles.signOutItemContainer : undefined;
  const signedOutFooterContainerStyle =
    isAccountUpdatesEnabled && currentBreakpoint.isXS ? styles.signedOutFooterContainer : undefined;

  // ─── Signed In Variant ───────────────────────────────────────────────

  if (isLoggedIn) {
    return (
      <VStack
        testID="account-menu"
        style={menuContainerResponsiveStyle}
        gap={menuContainerGap}
        hasDivider={!isAccountUpdatesEnabled || minWidth.isSM}
        dividerColor={dividerColor}
        {...a11ySelector.props}
      >
        {shouldRenderCustomerName ? (
          <View style={nameContainerStyle}>
            <AccountCustomerName />
          </View>
        ) : null}

        {shouldRenderGiftCardHeader ? <AccountMenuGiftCardHeader /> : null}

        <AccountPrimaryMenu
          screenId={screenId}
          gap={primaryMenuGap}
          hasDivider={match([!isAccountUpdatesEnabled, false])}
          dividerColor={theme.colors.LIGHT}
        />

        {Platform.OS === 'web' ? null : (
          <View style={isAccountUpdatesEnabled ? styles.secondaryMenuContainer : undefined}>
            <AccountSecondaryMenu />
          </View>
        )}

        <View style={signOutItemContainerStyles ? styles.signOutItemContainer : undefined}>
          <AccountMenuSignOutItem
            isSigningOut={isSigningOut}
            resetNavigation={resetStacksPostLogout}
          />
        </View>

        <AccountMenuFooter />
      </VStack>
    );
  }

  // ─── Signed Out Variant ──────────────────────────────────────────────

  return (
    <VStack
      testID="account-menu"
      style={menuContainerResponsiveStyle}
      gap={menuContainerGap}
      hasDivider={!isAccountUpdatesEnabled || minWidth.isSM}
      dividerColor={dividerColor}
    >
      <AccountSignedOutMessage />

      {Platform.OS === 'web' ? null : (
        <View style={isAccountUpdatesEnabled ? styles.secondaryMenuContainer : undefined}>
          <AccountSecondaryMenu />
        </View>
      )}

      <View style={signedOutFooterContainerStyle}>
        <AccountMenuFooter />
      </View>
    </VStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  menuContainerXS: {
    paddingVertical: theme.spacing['6'],
  },
  menuContainerSM: {
    paddingVertical: theme.spacing['12'],
    paddingHorizontal: theme.spacing['10'],
  },
  nameContainerXS: {
    paddingBottom: theme.spacing['5'],
  },
  nameContainerSM: {
    paddingBottom: theme.spacing['1'],
  },
  secondaryMenuContainer: {
    borderTopWidth: 1,
    borderColor: theme.colors.LIGHT,
    paddingTop: theme.spacing['4'],
  },
  signOutItemContainer: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.colors.LIGHT,
    paddingVertical: theme.spacing['4'],
  },
  signedOutFooterContainer: {
    borderTopWidth: 1,
    borderColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  screen?: AccountTabScreenId;
};
